import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Typography,
  InputAdornment,
  MenuItem
} from '@mui/material';
import { LocationOn as LocationIcon } from '@mui/icons-material';
import { ftthArmarioService, FtthArmario } from '../../services/ftthArmarioService';
import { MapSelectorModal } from '../common';
import EscritorioSelect from '../selects/EscritorioSelect';
import { getArmarioIconDataUrl, ARMARIO_ICON_OPTIONS } from '../../utils/armarioIcons';

const COLOR_PALETTE = [
  '#666666', '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF',
  '#FFD700', '#FFA500', '#FF6347', '#DC143C', '#006400', '#228B22', '#4169E1', '#4B0082',
  '#800080', '#808080', '#2F4F4F', '#696969',
];

const STATUS_OPTIONS = [
  { value: 'ativo', label: 'Ativo' },
  { value: 'inativo', label: 'Inativo' },
  { value: 'aguardando_instalacao', label: 'Aguardando Instalação' },
  { value: 'manutencao', label: 'Manutenção' },
];

export interface FtthArmarioFormProps {
  onSuccess?: (armario: FtthArmario) => void;
  onCancel?: () => void;
  armario?: FtthArmario;
  coordinates?: { lat: number; lng: number };
  isFullscreen?: boolean;
}

interface FtthArmarioFormData {
  name: string;
  address: string;
  escritorio_id: number;
  latitude: string;
  longitude: string;
  status: string;
  color: string;
  icon: string;
  color_text: string;
}

const FtthArmarioForm: React.FC<FtthArmarioFormProps> = ({
  onSuccess,
  onCancel,
  armario,
  coordinates,
  isFullscreen
}) => {
  const [formData, setFormData] = useState<FtthArmarioFormData>({
    name: armario?.name ?? '',
    address: armario?.address ?? '',
    escritorio_id: armario?.escritorio_id ?? 0,
    latitude: coordinates?.lat != null ? coordinates.lat.toString() : (armario?.latitude != null ? armario.latitude.toString() : ''),
    longitude: coordinates?.lng != null ? coordinates.lng.toString() : (armario?.longitude != null ? armario.longitude.toString() : ''),
    status: armario?.status ?? 'ativo',
    color: armario?.color ?? '#FF0000',
    icon: armario?.icon ?? 'icon01',
    color_text: armario?.color_text ?? '#FFFFFF',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    if (armario || coordinates) {
      setFormData((prev) => ({
        ...prev,
        ...(armario && {
          name: armario.name ?? '',
          address: armario.address ?? '',
          escritorio_id: armario.escritorio_id ?? 0,
          status: armario.status ?? 'ativo',
          color: armario.color ?? '#FF0000',
          icon: armario.icon ?? 'icon01',
          color_text: armario.color_text ?? '#FFFFFF',
        }),
        ...(coordinates && {
          latitude: coordinates.lat.toString(),
          longitude: coordinates.lng.toString(),
        }),
      }));
    }
  }, [armario, coordinates]);

  const handleChange = (field: keyof FtthArmarioFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleEscritorioChange = (value: number | number[] | null) => {
    const escritorioId = Array.isArray(value) ? (value[0] ?? null) : value;
    setFormData((prev) => ({ ...prev, escritorio_id: escritorioId ?? 0 }));
  };

  const handleOpenMapModal = () => {
    if (formData.latitude && formData.longitude) {
      const lat = parseFloat(formData.latitude);
      const lng = parseFloat(formData.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        setSelectedLocation({ lat, lng });
      } else {
        setSelectedLocation({ lat: -14.235004, lng: -51.92528 });
      }
    } else {
      setSelectedLocation({ lat: -14.235004, lng: -51.92528 });
    }
    setMapModalOpen(true);
  };

  const handleConfirmMapLocation = (lat: number, lng: number) => {
    setFormData((prev) => ({
      ...prev,
      latitude: lat.toString(),
      longitude: lng.toString(),
    }));
    setMapModalOpen(false);
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError('Nome é obrigatório');
      return false;
    }
    if (!formData.escritorio_id) {
      setError('Escritório é obrigatório');
      return false;
    }
    if ((formData.latitude && !formData.longitude) || (!formData.latitude && formData.longitude)) {
      setError('Informe ambas as coordenadas (latitude e longitude)');
      return false;
    }
    if (formData.latitude && formData.longitude) {
      const lat = parseFloat(formData.latitude);
      const lng = parseFloat(formData.longitude);
      if (isNaN(lat) || lat < -90 || lat > 90) {
        setError('Latitude deve estar entre -90 e 90');
        return false;
      }
      if (isNaN(lng) || lng < -180 || lng > 180) {
        setError('Longitude deve estar entre -180 e 180');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!validateForm()) return;

    try {
      setLoading(true);
      /*const payload = {
        name: formData.name.trim(),
        address: formData.address.trim() || null,
        escritorio_id: formData.escritorio_id,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        status: formData.status,
        color: formData.color.trim() || '#FF0000',
        icon: formData.icon,
        color_text: formData.color_text.trim() || '#FFFFFF',
      };

      const submitData = {
        name: payload.name,
        escritorio_id: payload.escritorio_id,
        status: payload.status as 'ativo' | 'inativo' | 'aguardando_instalacao' | 'manutencao',
        color: payload.color,
        icon: payload.icon,
        color_text: payload.color_text,
        address: payload.address ?? undefined,
        latitude: payload.latitude ?? undefined,
        longitude: payload.longitude ?? undefined
      };*/

      const submitData = {
        name: formData.name.trim(),
        escritorio_id: formData.escritorio_id,
        status: formData.status as 'ativo' | 'inativo' | 'aguardando_instalacao' | 'manutencao',
        color: formData.color.trim() || '#FF0000',
        icon: formData.icon,
        color_text: formData.color_text.trim() || '#FFFFFF',
        address: formData.address.trim() || undefined,
        latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
        longitude: formData.longitude ? parseFloat(formData.longitude) : undefined,
      };

      const result = armario
        ? await ftthArmarioService.update(armario.id, submitData)
        : await ftthArmarioService.create(submitData);

      const data = (result as any)?.data ?? result;
      if (data && onSuccess) {
        onSuccess(data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Erro ao salvar armário');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {armario ? 'Editar Armário' : 'Novo Armário'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Nome"
            value={formData.name}
            onChange={handleChange('name')}
            required
            size="small"
            placeholder="Ex: ARM-001"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <EscritorioSelect
            value={formData.escritorio_id || null}
            onChange={handleEscritorioChange}
            size="small"
            fullWidth
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Endereço"
            value={formData.address}
            onChange={handleChange('address')}
            size="small"
            placeholder="Endereço do armário"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            select
            label="Status"
            value={formData.status}
            onChange={handleChange('status')}
            size="small"
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>
            Localização
          </Typography>
        </Grid>
        <Grid item xs={12} sm={5}>
          <TextField
            fullWidth
            label="Latitude"
            value={formData.latitude}
            onChange={handleChange('latitude')}
            size="small"
            type="text"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button size="small" startIcon={<LocationIcon />} onClick={handleOpenMapModal} sx={{ minWidth: 0, px: 1 }}>
                    Mapa
                  </Button>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={5}>
          <TextField
            fullWidth
            label="Longitude"
            value={formData.longitude}
            onChange={handleChange('longitude')}
            size="small"
            type="text"
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
            Ícone no mapa
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {ARMARIO_ICON_OPTIONS.map((opt) => (
              <Box
                key={opt.id}
                onClick={() => setFormData((prev) => ({ ...prev, icon: opt.id }))}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  p: 1,
                  borderRadius: 1,
                  border: 2,
                  borderColor: formData.icon === opt.id ? 'primary.main' : 'divider',
                  bgcolor: formData.icon === opt.id ? 'action.selected' : 'transparent',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Box
                  component="img"
                  src={getArmarioIconDataUrl(formData.color, opt.id)}
                  alt={opt.label}
                  sx={{ width: 40, height: 40, objectFit: 'contain' }}
                />
                <Typography variant="caption" sx={{ mt: 0.5, fontSize: 10 }}>
                  {opt.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
            Cores
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <Typography variant="caption">Marcador:</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {COLOR_PALETTE.map((c) => (
                <Box
                  key={c}
                  onClick={() => setFormData((prev) => ({ ...prev, color: c }))}
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: 0.5,
                    bgcolor: c,
                    border: formData.color === c ? 3 : 1,
                    borderColor: formData.color === c ? 'primary.main' : 'divider',
                    cursor: 'pointer',
                  }}
                />
              ))}
              <TextField
                size="small"
                value={formData.color}
                onChange={handleChange('color')}
                type="color"
                sx={{ width: 36, height: 24, p: 0, minWidth: 36, '& input': { cursor: 'pointer', height: 24, p: 0 } }}
              />
            </Box>
            <Typography variant="caption" sx={{ ml: 2 }}>Texto:</Typography>
            <TextField
              size="small"
              value={formData.color_text}
              onChange={handleChange('color_text')}
              type="color"
              sx={{ width: 36, height: 24, p: 0, minWidth: 36, '& input': { cursor: 'pointer', height: 24, p: 0 } }}
            />
          </Box>
        </Grid>

        <Grid item xs={12} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
          {onCancel && (
            <Button variant="outlined" onClick={onCancel} disabled={loading}>
              Cancelar
            </Button>
          )}
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : armario ? 'Salvar' : 'Cadastrar'}
          </Button>
        </Grid>
      </Grid>

      {selectedLocation && (
        <MapSelectorModal
          open={mapModalOpen}
          onClose={() => setMapModalOpen(false)}
          initialCoordinates={selectedLocation}
          onConfirm={handleConfirmMapLocation}
        />
      )}
    </Box>
  );
};

export default FtthArmarioForm;
